import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";

const Teleprompter = forwardRef(
  (
    {
      text,
      speed = 100,
      initialMarginTop = 0,
      paddingLeft = 10,
      paddingRight = 10,
      bottomMargin = 75,
      fontSize = 24,
      countdownInit = 5,
      startMode = "countdown",
      mirrorHorizontal = false,
      mirrorVertical = false,
      invertColors = false,
    },
    ref
  ) => {
    const [isScrolling, setIsScrolling] = useState(false);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [isCountingDown, setIsCountingDown] = useState(false);
    const [countdown, setCountdown] = useState(null);
    const [awaitingStart, setAwaitingStart] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
      const handler = () => setIsFullscreen(!!document.fullscreenElement);
      document.addEventListener("fullscreenchange", handler);
      return () => document.removeEventListener("fullscreenchange", handler);
    }, []);

    const exitFullscreen = () => {
      const fn =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen;
      if (fn) fn.call(document);
    };

    const scrollRef = useRef(null);
    const containerRef = useRef(null);
    const countdownRef = useRef(null);

    // --- Fullscreen ---
    const goFullscreen = async () => {
      const el = containerRef.current;
      if (!el || document.fullscreenElement) return;
      const req =
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.mozRequestFullScreen ||
        el.msRequestFullscreen;
      try {
        if (req) await req.call(el);
      } catch (_) {
        /* quietly ignore */
      }
    };

    const anchorToTop = () => {
      const el = scrollRef.current;
      if (!el) return;
      const max = Math.max(0, el.scrollHeight - el.clientHeight);
      el.scrollTop = mirrorVertical ? max : 0;
    };

    const beginCountdown = () => {
      countdownRef.current = countdownInit;
      setCountdown(countdownInit);
      setIsCountingDown(true);
    };

    // --- Imperative API (for external buttons) ---
    const start = async () => {
      await goFullscreen();

      // stop any motion and reset logical position
      setIsScrolling(false);
      setIsCountingDown(false);
      setCountdown(null);
      setScrollPosition(0);

      // jump to the visual top immediately + after next paint (robust on size changes)
      anchorToTop();
      requestAnimationFrame(anchorToTop);

      if (startMode === "countdown") {
        beginCountdown();
        setAwaitingStart(false);
      } else {
        // startbutton or countdownAndStartbutton: wait for the in-display button
        setAwaitingStart(true);
      }
    };

    const handleInDisplayStart = () => {
      setAwaitingStart(false);
      if (startMode === "countdownAndStartbutton") {
        beginCountdown();
      } else {
        setIsScrolling(true);
      }
    };

    const pause = () => setIsScrolling(false);
    const play = () => setIsScrolling(true);
    const toggle = () => setIsScrolling((s) => !s);

    const restart = () => {
      setIsScrolling(false);
      setIsCountingDown(false);
      setCountdown(null);
      setAwaitingStart(false);
      setScrollPosition(0);

      anchorToTop();
      requestAnimationFrame(anchorToTop);
    };

    useImperativeHandle(
      ref,
      () => ({
        start,
        pause,
        play,
        toggle,
        restart,
        getState: () => ({ isScrolling, isCountingDown, countdown }),
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isScrolling, isCountingDown, countdown, countdownInit, startMode]
    );

    // --- Countdown -> start scrolling ---
    useEffect(() => {
      if (!isCountingDown) return;
      const id = setInterval(() => {
        countdownRef.current -= 1;
        if (countdownRef.current <= 0) {
          clearInterval(id);
          setIsCountingDown(false);
          setCountdown(null);
          setIsScrolling(true);
        } else {
          setCountdown(countdownRef.current);
        }
      }, 1000);
      return () => clearInterval(id);
    }, [isCountingDown]);

    // --- Scrolling loop ---
    useEffect(() => {
      if (!isScrolling) return;
      const interval = setInterval(() => {
        setScrollPosition((p) => p + 1);
      }, speed);
      return () => clearInterval(interval);
    }, [isScrolling, speed]);

    // --- Apply scrollTop (invert mapping when mirrorVertical) ---
    useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      const max = Math.max(0, el.scrollHeight - el.clientHeight);
      el.scrollTop = mirrorVertical ? max - scrollPosition : scrollPosition;
    }, [scrollPosition, mirrorVertical]);

    // --- Re-anchor on mirror toggle or content changes ---
    useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      const max = Math.max(0, el.scrollHeight - el.clientHeight);
      el.scrollTop = mirrorVertical ? max : 0;
    }, [mirrorVertical, text, fontSize, bottomMargin]);

    // Mirroring transforms (keep text upright when vertical mirroring)
    const scrollAreaTransform = mirrorVertical ? "rotate(180deg)" : undefined;
    const contentTransform = mirrorVertical
      ? `rotate(180deg) ${mirrorHorizontal ? "scaleX(-1)" : ""}`.trim()
      : mirrorHorizontal
      ? "scaleX(-1)"
      : undefined;

    return (
      <div
        className="teleprompter-container"
        ref={containerRef}
        style={{
          position: "relative",
          backgroundColor: invertColors ? "#ffffff" : undefined,
          color: invertColors ? "#111827" : undefined,
        }}
      >
        {/* Exit fullscreen button */}
        {isFullscreen && (
          <button
            onClick={exitFullscreen}
            title="Exit fullscreen"
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 20,
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "none",
              cursor: "pointer",
              background: "rgba(0,0,0,0.5)",
              color: "#fff",
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        )}

        {/* Countdown overlay */}
        {isCountingDown && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.6)",
              zIndex: 10,
            }}
          >
            <div
              style={{
                fontSize: "6rem",
                color: "#fff",
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {countdown}
            </div>
          </div>
        )}

        {/* In-display start button overlay */}
        {awaitingStart && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.6)",
              zIndex: 11,
            }}
          >
            <button
              onClick={handleInDisplayStart}
              style={{
                padding: "18px 48px",
                borderRadius: 12,
                border: "none",
                cursor: "pointer",
                background: "#038edc",
                color: "#fff",
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 700,
                fontSize: 22,
                boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
              }}
            >
              Start
            </button>
          </div>
        )}

        <div
          className="teleprompter-text"
          ref={scrollRef}
          style={{
            marginTop: `${initialMarginTop}px`,
            overflow: "auto",
            transform: scrollAreaTransform,
            transformOrigin: "center center",
            paddingBottom: "150px",
            paddingTop: "150px",
            paddingLeft: `${paddingLeft}px`,
            paddingRight: `${paddingRight}px`,
          }}
        >
          {/* Content only (controls live outside) */}
          <div
            className="teleprompter-content"
            style={{
              transform: contentTransform,
              transformOrigin: "center center",
            }}
          >
            {text?.split("\n").map((line, i) => (
              <p
                key={i}
                style={{
                  marginBottom: `${bottomMargin}px`,
                  fontSize: `${fontSize ?? 24}px`,
                }}
              >
                {line}
              </p>
            ))}
          </div>
        </div>
      </div>
    );
  }
);

Teleprompter.displayName = "Teleprompter";
export default Teleprompter;
